import { useRouteError, isRouteErrorResponse } from "react-router";
import { useEffect } from "react";
import {
  IllustrationContainer, Main, Page, PulseBlob, Actions, BigCircle, DecoBlob1, DecoBlob2, DecoLayer, ErrorDetailsButton, GradientButton, Group, Hidden,
  OutlineLink, Secondary, ShadowLine, Subtitle, TextBlock, Title
} from "./style";

import type { ErrorInfo, Props } from "./types";
import { slackLogger } from "../../services/slack_logger";
import ArrowCounterClockWiseIcon from "../../assets/icons/ArrowCounterClockWiseIcon";
import ExclamationRed from "../../assets/icons/ExclamationRed";


function getErrorInfo(error: unknown): ErrorInfo {
  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case 404:
        return {
          status: 404,
          title: "Página não encontrada",
          message: "A página que você está procurando não existe ou foi movida.",
        };
      case 401:
        return {
          status: 401,
          title: "Não autorizado",
          message: "Você precisa fazer login para acessar esta página.",
        };
      case 403:
        return {
          status: 403,
          title: "Acesso negado",
          message: "Você não tem permissão para acessar esta página.",
        };
      case 500:
        return {
          status: 500,
          title: "Erro interno",
          message: "Ocorreu um erro inesperado. Nossa equipe já foi notificada.",
        };
      default:
        return {
          status: error.status,
          title: "Algo deu errado",
          message: error.statusText || "Ocorreu um erro inesperado.",
        };
    }
  }

  return {
    status: 500,
    title: "Erro inesperado",
    message: "Ocorreu um erro inesperado. Por favor, tente novamente.",
  };
}

export const ErrorBoundary: React.FC<Props> = ({
  referenceId: _referenceId,
  onTryAgain,
  homeHref = "/",
}) => {

  const error = useRouteError();
  const errorInfo = getErrorInfo(error);
  // Optional: keep the HTML "lang=en" vibe — you can remove this if you want.
  useEffect(() => {
    document.title = "Something Went Wrong | Error";
  }, []);

  const handleTryAgain = () => {
    if (onTryAgain) return onTryAgain();
    window.location.reload();
  };

  useEffect(() => {
    let user = null;
    const cachedUser = localStorage.getItem("auth:user");
    if (cachedUser) user = JSON.parse(cachedUser);

    slackLogger.notify({
      message: `Critical Error (Boundary): ${errorInfo.title}`,
      error: error,
      user,
      context: {
        status: errorInfo.status,
        url: window.location.href
      },
      level: "fatal"
    });
  }, [error, errorInfo]);

  return (
    <>
      {/* <GlobalStyle /> */}
      <Page>
        <Main>
          <IllustrationContainer>
            <PulseBlob />
            <Group>
              <BigCircle>
                <ExclamationRed />
              </BigCircle>
              <ShadowLine />
            </Group>
          </IllustrationContainer>

          <TextBlock>
            <Title>{errorInfo.title}</Title>
            <Subtitle>
              {errorInfo.message}
            </Subtitle>
          </TextBlock>

          <Actions>
            <GradientButton onClick={handleTryAgain}>
              <ArrowCounterClockWiseIcon />
              Tente Novamente
            </GradientButton>

            <OutlineLink href={homeHref}>
              Voltar
            </OutlineLink>
          </Actions>

          <Secondary>
            <ErrorDetailsButton
              type="button"
              onClick={() => {
                // hook your “details” UI here if you want
                // e.g. open a modal, copy ref ID, etc.
              }}
            >
            </ErrorDetailsButton>

          </Secondary>
        </Main>

        <DecoLayer>
          <DecoBlob1 />
          <DecoBlob2 />
        </DecoLayer>

        {/* Hidden image placeholder exactly like your HTML */}
        <Hidden>
          <img
            alt="Error Graphic"
            data-alt="Abstract minimalist modern error page illustration"
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuBRII_iRBZHaUTkTn-6qFOKNa9uJQFregtdW4QBNS6ncDIPnSbtf8OiN3e3Np2dOb1z2hSZ76LFiAWIvvgHzJsAL0AeNuZNMFLKwM9GpQfi4UpYgquZgCOYv3ibbxDXalDE_fEXrraMMGv7OkGrCB6TZ6uXCSv0_j5ooGW2K7KBEPfHnZhnH67yzE0ooPnmtzBJ9iOPL5HdypJ6GLyStNL7V6pC91GvwbkeWpAP4EaX8A_05R5zwFyw8ZGWC9AtbREcULHNcc1oMfQ"
          />
        </Hidden>
      </Page>
    </>
  );
};
export default ErrorBoundary;
